import { Link as RouterLink, useLocation } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// assets
import AuditOutlined from '@ant-design/icons/AuditOutlined';
import BankOutlined from '@ant-design/icons/BankOutlined';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import ReconciliationOutlined from '@ant-design/icons/ReconciliationOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import bhsLogo from 'assets/images/brand/bhs-logo.svg';
import { useTranslation } from 'contexts/LanguageContext';
import { bhsTokens } from 'themes/tokens';

export const drawerWidth = 260;
export const collapsedDrawerWidth = 76;
const logoutSectionHeight = 40;

const menuItems = [
  { label: 'Hotels', path: '/hotels/details', icon: BankOutlined },
  { label: 'Clients', path: '/clients', icon: TeamOutlined },
  { label: 'Room Reservation', path: '/room-reservation', icon: CalendarOutlined },
  { label: 'Invoice', path: '/invoice', icon: FileTextOutlined },
  { label: 'CMS', path: '/cms', icon: AuditOutlined },
  { label: 'Invoice on account', path: '/invoice-on-account', icon: ReconciliationOutlined }
];

type SideMenuProps = {
  open: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
};

type MenuButtonProps = {
  expanded: boolean;
  label: string;
  path: string;
  icon: typeof BankOutlined;
  selected?: boolean;
  danger?: boolean;
  closeMenu?: () => void;
};

function MenuButton({ expanded, label, path, icon: Icon, selected = false, danger = false, closeMenu }: MenuButtonProps) {
  const { t } = useTranslation();

  return (
    <ListItemButton
      component={RouterLink}
      to={path}
      onClick={closeMenu}
      selected={selected}
      sx={{
        borderRadius: 1.5,
        minHeight: 46,
        px: expanded ? 1 : 0,
        justifyContent: expanded ? 'initial' : 'center',
        color: danger ? 'error.main' : 'text.secondary',
        bgcolor: 'transparent',
        transition: (theme) =>
          theme.transitions.create(['background-color', 'color'], {
            duration: theme.transitions.duration.shortest
          }),
        '&:hover': {
          bgcolor: danger ? 'transparent' : 'action.hover',
          color: danger ? 'error.main' : 'text.primary',
          '& .menu-icon-box': {
            bgcolor: 'background.paper',
            borderColor: danger ? 'error.light' : 'divider'
          }
        },
        '&.Mui-selected': {
          bgcolor: 'primary.lighter',
          color: 'primary.main',
          '&:hover': {
            bgcolor: 'primary.lighter'
          },
          '& .menu-icon-box': {
            bgcolor: 'primary.main',
            borderColor: 'primary.main',
            color: 'primary.contrastText'
          }
        }
      }}
    >
      <ListItemIcon sx={{ minWidth: expanded ? 44 : 'auto', justifyContent: 'center', color: 'inherit' }}>
        <Box
          className="menu-icon-box"
          sx={{
            width: bhsTokens.menu.iconBoxSize,
            height: bhsTokens.menu.iconBoxSize,
            display: 'grid',
            placeItems: 'center',
            border: '1px solid',
            borderColor: danger ? 'error.light' : selected ? 'primary.main' : 'transparent',
            borderRadius: 1.25,
            color: 'inherit',
            transition: (theme) =>
              theme.transitions.create(['background-color', 'border-color', 'color'], {
                duration: theme.transitions.duration.shortest
              }),
            '& svg': {
              fontSize: bhsTokens.menu.iconFontSize
            }
          }}
        >
          <Icon />
        </Box>
      </ListItemIcon>
      {expanded && (
        <ListItemText
          primary={t(label)}
          slotProps={{
            primary: {
              fontSize: bhsTokens.menu.itemFontSize,
              fontWeight: selected ? bhsTokens.menu.selectedItemFontWeight : bhsTokens.menu.itemFontWeight
            }
          }}
        />
      )}
    </ListItemButton>
  );
}

export default function SideMenu({ open, mobileOpen = false, onMobileClose }: SideMenuProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const width = open ? drawerWidth : collapsedDrawerWidth;

  const drawerContent = (expanded: boolean, closeMenu?: () => void) => (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Stack
        component={RouterLink}
        to="/hotels/details"
        direction="row"
        onClick={closeMenu}
        sx={(theme) => ({
          minHeight: 74,
          px: expanded ? 2 : 1,
          gap: 1.25,
          alignItems: 'center',
          justifyContent: expanded ? 'flex-start' : 'center',
          borderBottom: '1px solid',
          borderBottomColor: 'divider',
          textDecoration: 'none',
          flexShrink: 0
        })}
      >
        <Box
          component="img"
          src={bhsLogo}
          alt="Business Hotel Service International"
          sx={{ width: expanded ? 42 : 38, height: expanded ? 42 : 38, flexShrink: 0, display: 'block' }}
        />
        {expanded && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ color: 'text.primary', lineHeight: 1.25, fontWeight: 500 }}>
              Business Hotel Service
            </Typography>
            <Typography variant="subtitle1" sx={{ color: 'text.primary', lineHeight: 1.25, fontWeight: 500 }}>
              {t('International')}
            </Typography>
          </Box>
        )}
      </Stack>

      <Box
        sx={{
          px: expanded ? 2 : 1,
          py: 2,
          flexGrow: 1,
          minHeight: 0,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {menuItems.map((item) => {
            const selected = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

            return <MenuButton key={item.label} expanded={expanded} selected={selected} closeMenu={closeMenu} {...item} />;
          })}
        </List>
      </Box>

      <Box
        sx={{
          position: 'relative',
          height: logoutSectionHeight,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'error.lighter',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: -1,
            borderTop: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        <List sx={{ p: 0, px: expanded ? 2 : 1, width: '100%' }}>
          <MenuButton expanded={expanded} label="Logout" path="/login" icon={LogoutOutlined} danger closeMenu={closeMenu} />
        </List>
      </Box>
    </Stack>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            overflowX: 'hidden',
            overflowY: 'hidden'
          }
        }}
      >
        {drawerContent(true, onMobileClose)}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          width,
          flexShrink: 0,
          display: { xs: 'none', lg: 'block' },
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            overflowX: 'hidden',
            overflowY: 'hidden',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter
              })
          }
        }}
      >
        {drawerContent(open)}
      </Drawer>
    </>
  );
}
